import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { ToastrService } from 'ngx-toastr';
import { lastValueFrom } from 'rxjs';
import { HmrcWillsReport } from '../Models/HmrcWillsReport';
import { User } from '../Models/User';
import { UserService } from '../services/user.service';

@Component({
  selector: 'app-hmrc-wills-report',
  templateUrl: './hmrc-wills-report.component.html',
  styleUrls: ['./hmrc-wills-report.component.css'],
  standalone: false
})
export class HmrcWillsReportComponent implements OnInit, AfterViewInit {
  @Input({ required: true }) loggedInUser?: User = undefined;

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  displayedColumns: string[] = ['probateId', 'deceasedName', 'dateOfIssue', 'reportType', 'fileName'];

  allRecords: HmrcWillsReport[] = [];
  dataSource = new MatTableDataSource<HmrcWillsReport>([]);

  pageSize = 50;

  probateIdSearch: string = '';
  nameSearch: string = '';

  private loggedInUserTag = '';

  constructor(
    private http: HttpClient,
    private toastr: ToastrService,
    private userService: UserService
  ) {}

  ngOnInit() {
    const user = this.userService.loggedInUser;
    this.loggedInUserTag = `${user?.lastName}, ${user?.firstName}`;
    this.loadData();
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  async loadData() {
    try {
      const params = new HttpParams().set('user', this.loggedInUserTag);
      this.allRecords = await lastValueFrom(
        this.http.get<HmrcWillsReport[]>('/charitycentral/gethmrcwillsreport', { params })
      );
      this.applyFilters();
    } catch {
      this.toastr.error('Failed to load HMRC Wills report.', '', { timeOut: 4000 });
    }
  }

  applyFilters() {
    const idTerm = this.probateIdSearch.trim().toLowerCase();
    const nameTerm = this.nameSearch.trim().toLowerCase();

    const filtered = this.allRecords.filter(r => {
      const matchesId = !idTerm || r.probateId.toString().includes(idTerm);
      const matchesName = !nameTerm || r.deceasedName.toLowerCase().includes(nameTerm);
      return matchesId && matchesName;
    });

    this.dataSource.data = filtered;
    if (this.paginator) this.paginator.firstPage();
  }

  clearSearch() {
    this.probateIdSearch = '';
    this.nameSearch = '';
    this.applyFilters();
  }

  pageChanged(event: PageEvent) {
    this.pageSize = event.pageSize;
  }

  private formatDateForCsv(value: Date | string | null): string {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    const dd = d.getDate().toString().padStart(2, '0');
    const mm = (d.getMonth() + 1).toString().padStart(2, '0');
    return `${dd}/${mm}/${d.getFullYear()}`;
  }

  downloadInProgress = false;

  async downloadCsv() {
    // Re-fetch first, so a will fixed up (or newly flagged missing/incomplete) on the reporting
    // screen since this report was opened is reflected in the download rather than exporting
    // whatever was loaded at page-open time. applyFilters() (called inside loadData) then
    // re-applies the active search against the fresh data.
    this.downloadInProgress = true;
    try {
      await this.loadData();
    } finally {
      this.downloadInProgress = false;
    }

    // Export the current search filter AND the current column sort, so the CSV matches exactly
    // what's on screen rather than silently diverging from it.
    const rows = this.sort ? this.dataSource.sortData(this.dataSource.data, this.sort) : this.dataSource.data;
    if (!rows.length) {
      this.toastr.info('No data to download.', '', { timeOut: 3000 });
      return;
    }

    const header = 'Probate ID,Deceased Name,Date of Issue,Report Type,File Name';
    const lines = rows.map(r =>
      `${r.probateId},"${(r.deceasedName ?? '').replace(/"/g, '""')}",${this.formatDateForCsv(r.dateOfIssue)},"${(r.reportType ?? '').replace(/"/g, '""')}","${(r.fileName ?? '').replace(/"/g, '""')}"`
    );
    const csv = [header, ...lines].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hmrc_wills_report_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }
}
